import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { formatInTz, DEFAULT_TZ, TZ_OPTIONS } from "@/lib/tz";
import { Schedules, LiveScores, Results, Reconciliation, EventDetailModal, Standings, SyncCenter, ArticleDraftModal } from "./LiveWorkspaces";
import { Database, Plug, Link2, RefreshCw, AlertTriangle, Eye, ShieldCheck, CalendarDays, Radio, Trophy, GitCompare, ListOrdered } from "lucide-react";

const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function LiveData() {
  const [tab, setTab] = useState("schedules");
  const [meta, setMeta] = useState(null);
  const [comps, setComps] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tz, setTz] = useState(DEFAULT_TZ);
  const [openEvent, setOpenEvent] = useState(null);
  const [draftSel, setDraftSel] = useState(null);
  useEffect(() => {
    api.get("/live-data/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/sports-intel/competitions").then((r) => setComps(r.data)).catch(() => {});
    api.get("/live-data/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const TABS = [
    ["schedules", "Schedules", CalendarDays],
    ["live", "Live Scores", Radio],
    ["results", "Results", Trophy],
    ["standings", "Standings", ListOrdered],
    ["reconciliation", "Reconciliation", GitCompare],
    ["sync", "Sync Control Center", RefreshCw],
    ["providers", "Providers", Plug],
    ["mappings", "Team Mapping", Link2],
  ];
  return (
    <div className="space-y-6" data-testid="livedata-page">
      <PageHeader title="Sports Live Data" subtitle="Provider-neutral schedules, live status, results & standings — private intelligence, never auto-published.">
        <span className="text-xs px-2 py-1 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1" data-testid="ld-private-badge"><ShieldCheck className="h-3.5 w-3.5" /> Private</span>
      </PageHeader>
      <div className="flex gap-1 border-b border-zinc-800 flex-wrap">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`ld-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 flex items-center gap-1.5 transition-colors ${tab === k ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "schedules" && <Schedules tz={tz} setTz={setTz} onOpenEvent={setOpenEvent} />}
      {tab === "live" && <LiveScores tz={tz} setTz={setTz} onOpenEvent={setOpenEvent} settings={settings} />}
      {tab === "results" && <Results tz={tz} setTz={setTz} onOpenEvent={setOpenEvent} />}
      {tab === "standings" && <Standings onOpenDraft={setDraftSel} />}
      {tab === "reconciliation" && <Reconciliation />}
      {tab === "sync" && <SyncCenter meta={meta} comps={comps} settings={settings} />}
      {tab === "providers" && <Providers meta={meta} />}
      {tab === "mappings" && <Mappings comps={comps} />}
      <EventDetailModal eventId={openEvent} onClose={() => setOpenEvent(null)} tz={tz} onOpenDraft={setDraftSel} />
      <ArticleDraftModal selection={draftSel} onClose={() => setDraftSel(null)} />
    </div>
  );
}

function Providers({ meta }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", adapter_type: "mock", environment: "mock", status: "testing", base_url: "", credential_env_var: "", default_timeout: 15, max_requests_per_minute: 60, retry_count: 2, notes: "" });
  const load = useCallback(() => api.get("/live-data/providers").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      await api.post("/live-data/providers", form);
      toast.success("Provider created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const test = async (id) => {
    try { await api.post(`/live-data/providers/${id}/test-connection`, {}); toast.success("Connection OK"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/live-data/providers/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="ld-providers">
      <div className="flex justify-end"><Btn data-testid="ld-new-provider-btn" onClick={() => setOpen(true)}><Plug className="h-4 w-4" /> New Provider</Btn></div>
      <div className="grid gap-3 md:grid-cols-2">
        {rows.length === 0 && <p className="text-sm text-zinc-500">No providers configured. Create a deterministic mock provider to begin.</p>}
        {rows.map((p) => (
          <Card key={p.id} data-testid={`ld-provider-${p.id}`} className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-white flex items-center gap-2"><Database className="h-4 w-4 text-emerald-400" /> {p.name}</div>
              <span className={`text-xs px-2 py-0.5 rounded-sm border ${p.status === "active" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : p.status === "testing" ? "bg-amber-500/10 text-amber-300 border-amber-500/30" : "bg-zinc-700/30 text-zinc-400 border-zinc-700"}`} data-testid={`ld-provider-status-${p.id}`}>{p.status}</span>
            </div>
            <div className="text-xs text-zinc-400 grid grid-cols-2 gap-x-4 gap-y-1">
              <span>Adapter: <b className="text-zinc-200">{p.adapter_type}</b></span>
              <span>Env: <b className="text-zinc-200">{p.environment}</b></span>
              <span>Rate: <b className="text-zinc-200">{p.max_requests_per_minute}/min</b></span>
              <span>Retries: <b className="text-zinc-200">{p.retry_count}</b></span>
              <span>Credential: <b className="text-zinc-200">{p.credential_env_var || "—"}</b> {p.credential_present ? "✓" : ""}</span>
              <span>Auto-refresh: <b className="text-zinc-200">{p.auto_refresh_enabled ? "on" : "off"}</b></span>
              <span>Last OK: <b className="text-zinc-200">{p.last_success_at ? new Date(p.last_success_at).toLocaleString() : "—"}</b></span>
              <span>Last fail: <b className="text-zinc-200">{p.last_failure_at ? new Date(p.last_failure_at).toLocaleString() : "—"}</b></span>
            </div>
            {p.last_error && <div className="text-xs text-red-400" data-testid={`ld-provider-error-${p.id}`}>Error: {p.last_error}</div>}
            <div className="flex gap-2 pt-1">
              {p.adapter_type === "mock" && <Btn variant="ghost" className="text-xs py-1" data-testid={`ld-provider-test-${p.id}`} onClick={() => test(p.id)}>Test connection</Btn>}
              {p.adapter_type === "mock" && p.status !== "active" && <Btn variant="ghost" className="text-xs py-1" onClick={() => setStatus(p.id, "active")}>Activate</Btn>}
              {p.status === "active" && <Btn variant="ghost" className="text-xs py-1" onClick={() => setStatus(p.id, "disabled")}>Disable</Btn>}
            </div>
          </Card>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="New Live-Data Provider">
        <div className="space-y-3">
          <Field label="Name"><input data-testid="ld-provider-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Adapter type"><select data-testid="ld-provider-adapter" className={inputCls} value={form.adapter_type} onChange={(e) => setForm({ ...form, adapter_type: e.target.value })}>{(meta?.adapter_types || []).map((a) => <option key={a} value={a}>{a}</option>)}</select></Field>
            <Field label="Environment"><select className={inputCls} value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })}>{(meta?.environments || []).map((a) => <option key={a} value={a}>{a}</option>)}</select></Field>
            <Field label="Status"><select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{(meta?.provider_statuses || []).map((a) => <option key={a} value={a}>{a}</option>)}</select></Field>
            <Field label="Credential env-var name (server-side only)"><input data-testid="ld-provider-cred" className={inputCls} placeholder="e.g. SPORTS_API_KEY" value={form.credential_env_var} onChange={(e) => setForm({ ...form, credential_env_var: e.target.value })} /></Field>
            <Field label="Max requests / min"><input type="number" className={inputCls} value={form.max_requests_per_minute} onChange={(e) => setForm({ ...form, max_requests_per_minute: Number(e.target.value) })} /></Field>
            <Field label="Retry count"><input type="number" className={inputCls} value={form.retry_count} onChange={(e) => setForm({ ...form, retry_count: Number(e.target.value) })} /></Field>
          </div>
          <Field label="Base URL (optional)"><input className={inputCls} value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} /></Field>
          <p className="text-xs text-amber-300/80">The secret value is never entered or stored here — only the NAME of a server-side environment variable.</p>
          <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn data-testid="ld-provider-save" onClick={save} disabled={!form.name}>Create</Btn></div>
        </div>
      </Modal>
    </div>
  );
}

function Mappings({ comps }) {
  const [providers, setProviders] = useState([]);
  const [pid, setPid] = useState("");
  const [maps, setMaps] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [linkFor, setLinkFor] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  const [ents, setEnts] = useState([]);
  useEffect(() => { api.get("/live-data/providers").then((r) => { setProviders(r.data); if (r.data[0]) setPid(r.data[0].id); }); }, []);
  const load = useCallback(() => {
    if (!pid) return;
    api.get("/live-data/team-maps", { params: { provider_id: pid } }).then((r) => setMaps(r.data)).catch(() => {});
    api.get("/live-data/unmapped-teams", { params: { provider_id: pid } }).then((r) => setUnmapped(r.data)).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load]);
  const openLink = async (m) => {
    setLinkFor(m); setQ(""); setEnts([]);
    try { const r = await api.get(`/live-data/team-maps/${m.id}/suggestions`); setSuggestions(r.data); } catch { setSuggestions([]); }
  };
  const searchEnts = async (val) => {
    setQ(val);
    if (!val) return setEnts([]);
    try { const r = await api.get("/entities/entities", { params: { q: val } }); setEnts(r.data); } catch { setEnts([]); }
  };
  const doLink = async (entity_id) => {
    try { await api.post(`/live-data/team-maps/${linkFor.id}/link`, { entity_id, confidence: "high" }); toast.success("Team linked"); setLinkFor(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="ld-mappings">
      <Field label="Provider"><select data-testid="ld-map-provider" className={`${inputCls} max-w-xs`} value={pid} onChange={(e) => setPid(e.target.value)}>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
      <Card className="p-4" data-testid="ld-unmapped-queue">
        <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-amber-400" /><h3 className="font-semibold text-white">Unmapped Teams Queue</h3><span className="text-xs text-zinc-500">({unmapped.length})</span></div>
        {unmapped.length === 0 && <p className="text-sm text-zinc-500">No unmapped teams. Run a sync to populate provider teams, then link each to a canonical Entity Profile.</p>}
        <div className="space-y-2">
          {unmapped.map((m) => (
            <div key={m.id} data-testid={`ld-unmapped-${m.id}`} className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2">
              <div className="text-sm"><b className="text-white">{m.provider_team_name}</b> <span className="text-xs text-zinc-500">· {m.provider_team_id}</span> <span className="text-xs px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 ml-2">{m.status}</span></div>
              <Btn variant="ghost" className="text-xs py-1" data-testid={`ld-link-${m.id}`} onClick={() => openLink(m)}>Link to Entity</Btn>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-4" data-testid="ld-confirmed-maps">
        <h3 className="font-semibold text-white mb-3">Confirmed Team Mappings</h3>
        {maps.filter((m) => m.status === "confirmed").length === 0 && <p className="text-sm text-zinc-500">No confirmed mappings yet.</p>}
        <div className="space-y-1">
          {maps.filter((m) => m.status === "confirmed").map((m) => (
            <div key={m.id} className="flex items-center justify-between text-sm border-b border-zinc-900 py-1.5" data-testid={`ld-map-${m.id}`}>
              <span className="text-zinc-300">{m.provider_team_name}</span>
              <span className="text-emerald-300">→ {m.entity?.name} <span className="text-xs text-zinc-500">({m.entity?.entity_type})</span></span>
            </div>
          ))}
        </div>
      </Card>
      <Modal open={!!linkFor} onClose={() => setLinkFor(null)} title={`Link "${linkFor?.provider_team_name}" to a canonical entity`}>
        <div className="space-y-3">
          {suggestions.length > 0 && (
            <div><div className="label-caps text-zinc-500 mb-1">Suggestions</div>
              <div className="flex flex-wrap gap-2">{suggestions.map((sg) => <button key={sg.id} data-testid={`ld-suggest-${sg.id}`} onClick={() => doLink(sg.id)} className="text-xs px-2 py-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20">{sg.name}</button>)}</div>
            </div>
          )}
          <Field label="Search entities"><input data-testid="ld-ent-search" autoFocus className={inputCls} placeholder="Team / club name" value={q} onChange={(e) => searchEnts(e.target.value)} /></Field>
          <div className="max-h-52 overflow-y-auto space-y-1">
            {ents.length === 0 && <p className="text-xs text-zinc-600">No matches. Create the entity in Entity Profiles first — teams are never auto-created.</p>}
            {ents.map((e) => <button key={e.id} data-testid={`ld-ent-opt-${e.id}`} onClick={() => doLink(e.id)} className="w-full text-left text-sm px-2 py-1.5 rounded-sm hover:bg-zinc-900 flex items-center gap-2"><span>{e.display_name}</span><span className="text-xs text-zinc-500 ml-auto">{e.entity_type}</span></button>)}
          </div>
        </div>
      </Modal>
    </div>
  );
}

function SyncControl({ meta, comps }) {
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ provider_id: "", competition_id: "", data_type: "schedules", scenario: "" });
  const [runs, setRuns] = useState([]);
  const [events, setEvents] = useState([]);
  const [raw, setRaw] = useState(null);
  const [tz, setTz] = useState(DEFAULT_TZ);
  const [compMaps, setCompMaps] = useState([]);
  useEffect(() => { api.get("/live-data/providers").then((r) => { setProviders(r.data); if (r.data[0]) setForm((f) => ({ ...f, provider_id: r.data[0].id })); }); }, []);
  const loadRuns = useCallback(() => api.get("/live-data/sync-runs", { params: { limit: 25 } }).then((r) => setRuns(r.data)).catch(() => {}), []);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { if (form.provider_id) api.get("/live-data/competition-maps", { params: { provider_id: form.provider_id } }).then((r) => setCompMaps(r.data)).catch(() => {}); }, [form.provider_id]);
  const ensureMap = async () => {
    // auto-create competition map from the competition short_name (mock key) if missing
    const comp = comps.find((c) => c.id === form.competition_id);
    if (!comp) return;
    const exists = compMaps.find((m) => m.competition_id === form.competition_id);
    if (!exists) {
      await api.post("/live-data/competition-maps", { provider_id: form.provider_id, competition_id: form.competition_id, provider_competition_id: comp.short_name, provider_competition_key: comp.short_name });
      const r = await api.get("/live-data/competition-maps", { params: { provider_id: form.provider_id } });
      setCompMaps(r.data);
    }
  };
  const runSync = async () => {
    try {
      await ensureMap();
      const body = { provider_id: form.provider_id, competition_id: form.competition_id, data_type: form.data_type };
      if (form.scenario) body.scenario = form.scenario;
      const r = await api.post("/live-data/sync", body);
      toast.success(`Sync ${r.data.status}: +${r.data.records_inserted} / ~${r.data.records_updated}`);
      loadRuns(); loadEvents();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadEvents = useCallback(() => { if (form.competition_id) api.get("/live-data/events", { params: { provider_id: form.provider_id, competition_id: form.competition_id } }).then((r) => setEvents(r.data)).catch(() => {}); }, [form.provider_id, form.competition_id]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  const inspect = async (ref) => { try { const r = await api.get(`/live-data/raw-payloads/${ref}`); setRaw(r.data); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="space-y-4" data-testid="ld-sync">
      <Card className="p-4">
        <div className="grid md:grid-cols-5 gap-3 items-end">
          <Field label="Provider"><select data-testid="ld-sync-provider" className={inputCls} value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          <Field label="Competition"><select data-testid="ld-sync-comp" className={inputCls} value={form.competition_id} onChange={(e) => setForm({ ...form, competition_id: e.target.value })}><option value="">Select…</option>{comps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="Data type"><select data-testid="ld-sync-type" className={inputCls} value={form.data_type} onChange={(e) => setForm({ ...form, data_type: e.target.value })}>{(meta?.data_types || []).map((d) => <option key={d} value={d}>{pretty(d)}</option>)}</select></Field>
          <Field label="Scenario (mock)"><select data-testid="ld-sync-scenario" className={inputCls} value={form.scenario} onChange={(e) => setForm({ ...form, scenario: e.target.value })}><option value="">normal</option><option value="flaky">flaky (retry)</option><option value="error">error (fail)</option></select></Field>
          <Btn data-testid="ld-run-sync" onClick={runSync} disabled={!form.provider_id || !form.competition_id}><RefreshCw className="h-4 w-4" /> Run Manual Sync</Btn>
        </div>
        <p className="text-xs text-zinc-500 mt-2">Automatic polling is OFF in preview — every sync here is manual. Intervals (live {meta?.default_intervals?.live}s / scheduled {meta?.default_intervals?.schedules}s / standings {meta?.default_intervals?.standings}s) apply only when auto-refresh is enabled.</p>
      </Card>

      {events.length > 0 && (
        <Card className="p-4" data-testid="ld-events-preview">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-white">Ingested Events</h3>
            <Field label="Display timezone"><select data-testid="ld-tz-select" className={`${inputCls} py-1`} value={tz} onChange={(e) => setTz(e.target.value)}>{TZ_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
          </div>
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-left"><th className="py-1 pr-3">Start ({tz.split("/")[1]})</th><th className="pr-3">Match</th><th className="pr-3">Status</th><th className="pr-3">Score</th><th className="pr-3">Mapping</th><th>Fresh</th></tr></thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} data-testid={`ld-event-${e.id}`} className="border-t border-zinc-900">
                  <td className="py-1.5 pr-3 text-zinc-300" data-testid={`ld-event-time-${e.id}`}>{formatInTz(e.scheduled_utc, tz)}</td>
                  <td className="pr-3 text-zinc-200">{e.home_team?.name || e.home_team_provider_name} <span className="text-zinc-600">v</span> {e.away_team?.name || e.away_team_provider_name}</td>
                  <td className="pr-3"><span className={`px-1.5 py-0.5 rounded-sm ${["in_progress", "halftime", "overtime", "intermission"].includes(e.status) ? "bg-emerald-500/10 text-emerald-300" : e.status.startsWith("final") ? "bg-sky-500/10 text-sky-300" : "bg-zinc-700/30 text-zinc-400"}`}>{pretty(e.status)}</span></td>
                  <td className="pr-3 text-zinc-300">{e.home_score != null ? `${e.home_score}-${e.away_score}` : "—"}{e.penalty_score ? ` (pens ${e.penalty_score})` : e.extra_time_score ? ` (aet ${e.extra_time_score})` : ""}</td>
                  <td className="pr-3">{e.mapping_incomplete ? <span className="text-amber-400" data-testid={`ld-event-mapwarn-${e.id}`}>⚠ unmapped</span> : <span className="text-emerald-400">✓</span>}</td>
                  <td>{e.is_stale ? <span className="text-red-400" data-testid={`ld-event-stale-${e.id}`}>stale</span> : <span className="text-emerald-500">fresh</span>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      )}

      <Card className="p-4" data-testid="ld-sync-runs">
        <h3 className="font-semibold text-white mb-3">Recent Sync Runs</h3>
        <div className="overflow-x-auto"><table className="w-full text-xs">
          <thead><tr className="text-zinc-500 text-left"><th className="py-1 pr-3">When</th><th className="pr-3">Provider</th><th className="pr-3">Type</th><th className="pr-3">Status</th><th className="pr-3">In/Up/Un/Rej</th><th className="pr-3">Conflicts</th><th className="pr-3">Dur</th><th>Raw</th></tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} data-testid={`ld-run-${r.id}`} className="border-t border-zinc-900">
                <td className="py-1.5 pr-3 text-zinc-400">{new Date(r.created_at).toLocaleTimeString()}</td>
                <td className="pr-3 text-zinc-300">{r.provider_name}</td>
                <td className="pr-3">{pretty(r.data_type)}</td>
                <td className="pr-3"><span className={r.status === "success" ? "text-emerald-400" : "text-red-400"} data-testid={`ld-run-status-${r.id}`}>{r.status}</span></td>
                <td className="pr-3 text-zinc-300">{r.records_inserted}/{r.records_updated}/{r.records_unchanged}/{r.records_rejected}</td>
                <td className="pr-3 text-zinc-400">m{r.mapping_conflicts} d{r.duplicate_conflicts}</td>
                <td className="pr-3 text-zinc-500">{r.duration_ms}ms</td>
                <td>{r.raw_payload_ref ? <button data-testid={`ld-raw-${r.id}`} onClick={() => inspect(r.raw_payload_ref)} className="text-sky-400 hover:text-sky-300"><Eye className="h-3.5 w-3.5" /></button> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      <Modal open={!!raw} onClose={() => setRaw(null)} title="Raw Provider Payload" wide>
        <pre className="text-xs text-emerald-200 bg-black/40 p-3 rounded-sm overflow-auto max-h-[60vh]" data-testid="ld-raw-json">{raw ? JSON.stringify(raw.payload, null, 2) : ""}</pre>
      </Modal>
    </div>
  );
}
